import { AxiosError, AxiosResponse } from 'axios';
import { useMutation, useQuery } from 'react-query';
import apiClient from '../apiClient/apiClient';
import {
  Menu,
  MenuItemRecipe,
  MenuItemSubrecipe,
  MenuItemSubrecipeSpecRequestBody,
  MenuRequestBody,
  MenusRequestBody,
  MenusUpdateRequestBody,
  CompareMenuRecipe,
  CompareMenuSubrecipe, MenuTreeResponse, MenuTreeFilter,
  Organization
} from './menu.types';

/** cacheTime number equivalent to the length of time before inactive data gets removed from the cache, time unit is ms  */
export const useMenus = (requestBody: { storeNo?: string, activateOrExtract?: string }, enabled: boolean = false, cacheTime: number = 0) => {
  return useQuery<AxiosResponse<Menu[]>>({
    queryKey: ['menus', requestBody.storeNo],
    queryFn: () => apiClient.get<Menu[]>('/menus', { params: requestBody }),
    enabled,
    cacheTime: cacheTime
  });
};

export const useOrganizations = (requestBody: {activateOrExtract?: string},enabled: boolean = false, cacheTime: number = 0) =>{
  let params: any = {activateOrExtract: requestBody.activateOrExtract};
  return useQuery<AxiosResponse<Organization[]>>({
    queryKey: ['orgs', requestBody.activateOrExtract],
    queryFn: () => apiClient.get<Organization[]>('/menus/organizations', {params: params}),
    enabled,
    cacheTime: cacheTime,
  })
}

export const useOrganizationsDetails = (requestBody: {orgNo?: string, activateOrExtract?: string}, enabled?: boolean, cacheTime: number = 0) =>{
  return useQuery<AxiosResponse<Organization>>({
    queryKey: ['orgDetails', requestBody.activateOrExtract, requestBody.orgNo],
    queryFn: () => apiClient.get<Organization>(`/menus/organizations/${requestBody.orgNo}`, {params: {activateOrExtract: requestBody.activateOrExtract}}),
    enabled,
    cacheTime
  })
}

export const useMenusInOrg = (requestBody: { orgId?: string; activateOrExtract?: string;}, enabled?: boolean, cacheTime: number = 0) => {
  return useQuery<AxiosResponse<Organization[]>>({
    queryKey: ['menusInOrg', requestBody.activateOrExtract, requestBody.orgId],
    queryFn: () => apiClient.get<Organization[]>('/menus-in-org', {params: requestBody}),
    enabled,
    cacheTime
  })
}

export const fetchOrganizations = async (
  requestBody: {activateOrExtract?: string},
  filter?: any,
  filterKey?: string,
  onSuccess?: (response: AxiosResponse) => void,
  onError?: (error: AxiosError) => void
) => {
  try {
    let params: any = {activateOrExtract: requestBody.activateOrExtract};
    if(filter && filterKey){
      params[filterKey] = filter[filterKey]
    }
    const response = await apiClient.get<Organization[]>('/menus/organizations', {params: params});
    onSuccess && onSuccess(response);
  } catch (error) {
    onError && onError(error as AxiosError);
  }
}

 export const fetchMenusInOrganization = async (
  requestBody: { orgId?: string; activateOrExtract?: string;},
  onSuccess?: (response: AxiosResponse) => void,
  onError?: (error: AxiosError) => void
) => {
  try {
    const response = await apiClient.get<Organization[]>('/menus-in-org', { params: requestBody });
    onSuccess && onSuccess(response);
  } catch (error) {
    onError && onError(error as AxiosError);
  }
};

export const fetchMenusSearch = async (
  requestBody: { activateOrExtract?: string, menuNo: string},
  onSuccess?: (response: AxiosResponse) => void,
  onError?: (error: AxiosError) => void
) => {
  try {
    const response = await apiClient.get<Organization[]>(`/menus/search/`, {params: requestBody});
    onSuccess && onSuccess(response);
  } catch (error) {
    onError && onError(error as AxiosError);
  }
};

 export const useMenuSearch = (requestBody:{activateOrExtract?: string},menuNo: string, enabled: boolean = false) => {
  return useQuery<AxiosResponse<Organization[]>>({
    queryKey: ['menu', menuNo, requestBody.activateOrExtract],
    queryFn: () => apiClient.get<Organization[]>(`/menus/search/${menuNo}`, {params: requestBody}),
    enabled,
  });
};

export const useMenusInMarket = (requestBody?: MenuTreeFilter, enabled: boolean = false, cacheTime: number = 0) => {
  return useQuery<AxiosResponse<MenuTreeResponse[]>>({
    queryKey: ['menus-in-market', {marketNo: requestBody?.marketNo, storeNo: requestBody?.storeNo}],
    queryFn: () => apiClient.get<MenuTreeResponse[]>('/menus-in-market', { params: requestBody }),
    enabled,
    cacheTime: cacheTime
  });
};

export const useMenusCount = (requestParams: {statusNo?: number, isJoinMarkets?: boolean, activateOrExtract?: string}, enabled: boolean = false) => {
  return useQuery<AxiosResponse>({
    queryKey: ['menus-count', requestParams.statusNo, requestParams.isJoinMarkets, requestParams.activateOrExtract],
    queryFn: () => apiClient.head('/menus', { params: requestParams }),
    enabled,
  });
};

export const useEditMenus = () => {
  return useMutation((menus: MenusRequestBody) => {
    return apiClient.put(`/menus`, menus);
  });
};

export const useMenu = (menuNo: string, enabled: boolean = false) => {
  return useQuery<AxiosResponse<Menu>>({
    queryKey: ['menu', menuNo],
    queryFn: () => apiClient.get<Menu>(`/menus/${menuNo}`),
    enabled,
  });
};

export const useEditMenu = (menuNo: string) => {
  return useMutation((menu: MenuRequestBody) => {
    return apiClient.put(`/menus/${menuNo}`, menu);
  });
};

export const useBulkEditMenu = () => {
  return useMutation((menu: MenusUpdateRequestBody) => {
    return apiClient.patch(`/menus`, menu);
  });
};

export const useMenuItemRecipe = (
    menuNo: string | undefined,
    requestBody: {
      classNo?: number,
      baseNo?: number,
      sizeNo?: number,
      strategyNo?: string
    },
    enabled: boolean = false
) => {
  return useQuery<AxiosResponse<MenuItemRecipe[]>>({
    queryKey: ['menu-item-recipes', menuNo, requestBody.classNo, requestBody.baseNo, requestBody.sizeNo, requestBody.strategyNo],
    queryFn: () => apiClient.get<MenuItemRecipe[]>(`/menus/${menuNo}/items/recipes`, { params: requestBody }),
    enabled,
  });
};

export const useCompareMenuItemRecipe = (requestBody: { menuOneNo?: number, menuTwoNo?: number, classNo?: number }, enabled: boolean = false) => {
  return useQuery<AxiosResponse<CompareMenuRecipe>>({
    queryKey: ['menu-item-recipes', requestBody.menuOneNo, requestBody.menuTwoNo, requestBody.classNo, ],
    queryFn: () => apiClient.post<CompareMenuRecipe>(`/menus/compare/items/recipes`, requestBody),
    enabled,
  });
};
export const useCompareMenuItemSubrecipe = (requestBody: { menuOneNo?: number, menuTwoNo?: number, classNo?: number }, enabled: boolean = false) => {
  return useQuery<AxiosResponse<CompareMenuSubrecipe>>({
    queryKey: ['menu-item-subrecipes', requestBody.menuOneNo, requestBody.menuTwoNo, requestBody.classNo, ],
    queryFn: () => apiClient.post<CompareMenuSubrecipe>(`/menus/compare/items/subrecipes`, requestBody),
    enabled,
  });
};

export const useGetMenuItemSubRecipes = (
    menuNo: string | undefined,
    requestBody: {
      classNo?: number,
      baseNo?: number,
      sizeNo?: number
    },
    enabled: boolean = true
) => {
  return useQuery<AxiosResponse<MenuItemSubrecipe[]>>({
    queryKey: ['menu-item-sub-recipes', menuNo, requestBody.classNo, requestBody.baseNo, requestBody.sizeNo],
    queryFn: () => apiClient.get<MenuItemSubrecipe[]>(`/menus/${menuNo}/items/sub-recipes`, { params: requestBody }),
    enabled,
  });
};

export const useUpdateMenuItemRecipe = (menuNo: string, menuItemId: string) => {
  return useMutation((record: number | undefined) => {
    return apiClient.put(`menus/${menuNo}/items/recipes/${menuItemId}`, record);
  })
};

export const useUpdateMenuItemSubRecipe = (menuNo: string, menuItemId: string) => {
  return useMutation((record: MenuItemSubrecipeSpecRequestBody) => {
    return apiClient.put(`menus/${menuNo}/items/sub-recipes/${menuItemId}`, record);
  })
};

export const useMenusWithSpecNos = (requestBody: { specNos: string }, enabled: boolean = false, cacheTime: number = 0) => {
  return useQuery<AxiosResponse<Organization[]>>({
    queryKey: ['menus', requestBody.specNos],
    queryFn: () => apiClient.get<Organization[]>('/menus', { params: requestBody }),
    enabled,
    cacheTime: cacheTime
  })
};

export const useClearMissingSpecs = () => {
  return useMutation((menuNo: string) => {
    return apiClient.put(`/menus/clearnullspecs/${menuNo}`);
  });
};

export const fetchMenusInMarket = async (
  requestBody: { marketNo?: string; storeNo?: string; statusNo?: number;},
  onSuccess?: (response: AxiosResponse) => void,
  onError?: (error: AxiosError) => void
) => {
  try {
    const response = await apiClient.get<MenuTreeResponse[]>('/menus-in-market', { params: requestBody });
    onSuccess && onSuccess(response);
  } catch (error) {
    onError && onError(error as AxiosError);
  }
};
